/*
 * AdminOperations — Phase CC-3
 * 5-tab page closing the operational gaps: Notifications · Messages ·
 * WMS · Crons · Apps Catalog.
 */
import { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  Bell, MessageSquare, Warehouse, Timer, AppWindow, Loader2,
  Send, ShieldOff, Play, RotateCw, Pencil,
} from "lucide-react";


const TABS = [
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "messages",      label: "Messages",      icon: MessageSquare },
  { id: "wms",           label: "WMS",           icon: Warehouse },
  { id: "crons",         label: "Crons",         icon: Timer },
  { id: "appscatalog",   label: "Apps catalog",  icon: AppWindow },
];


export default function AdminOperations() {
  const [tab, setTab] = useState("notifications");
  return (
    <div className="space-y-5 max-w-7xl" data-testid="admin-operations">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Operations Console</h1>
        <p className="text-[12.5px] text-foreground/55">
          Notification log · message threads · WMS · cron triggers · runtime app catalog overrides.
        </p>
      </header>

      <div className="flex gap-1 border-b overflow-x-auto" data-testid="ops-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`ops-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-sm border-b-2 whitespace-nowrap ${
                tab === t.id ? "border-foreground text-foreground font-medium"
                              : "border-transparent text-foreground/55 hover:text-foreground/80"
              }`}>
              <Icon className="w-3.5 h-3.5"/>{t.label}
            </button>
          );
        })}
      </div>

      {tab === "notifications" && <NotificationsTab/>}
      {tab === "messages"      && <MessagesTab/>}
      {tab === "wms"           && <WmsTab/>}
      {tab === "crons"         && <CronsTab/>}
      {tab === "appscatalog"   && <AppsCatalogTab/>}
    </div>
  );
}


function Kpi({ label, value, tone = "neutral", testid }) {
  const tones = {
    neutral: "bg-foreground/3 border-foreground/10",
    emerald: "bg-emerald-50 border-emerald-200",
    amber:   "bg-amber-50 border-amber-200",
    rose:    "bg-rose-50 border-rose-200",
    sky:     "bg-sky-50 border-sky-200",
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[11px] text-foreground/55 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * 1. Notifications
 * ──────────────────────────────────────────────────────────────────── */
function NotificationsTab() {
  const [data, setData] = useState({ rows: [], total: 0, by_kind: {} });
  const [busy, setBusy] = useState(false);
  const [userId, setUserId] = useState("");
  const [kind, setKind] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ since_days: "30", limit: "100" });
      if (userId) params.set("user_id", userId);
      if (kind)   params.set("kind", kind);
      if (unreadOnly) params.set("unread_only", "true");
      const r = await api.get(`/admin/notifications?${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load notifications");
    } finally { setBusy(false); }
  }, [userId, kind, unreadOnly]);

  useEffect(() => { load(); }, [load]);

  const resend = async (nid) => {
    const reason = window.prompt("Reason for force-resending? (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/notifications/${nid}/force-resend`, { reason: reason.trim() });
      toast.success("Resent");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  const unread = Object.values(data.by_kind || {}).reduce((s, v) => s + (v.unread || 0), 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi label="Total (30d)" value={data.total ?? 0}/>
        <Kpi label="Unread" value={unread} tone={unread > 0 ? "amber" : "neutral"}/>
        <Kpi label="Distinct kinds" value={Object.keys(data.by_kind || {}).length}/>
        <Kpi label="Showing" value={data.rows?.length || 0}/>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[220px]">
          <Label htmlFor="notif-user">Filter by user_id</Label>
          <Input id="notif-user" value={userId} onChange={(e) => setUserId(e.target.value)}
            data-testid="notif-user-filter"/>
        </div>
        <div className="min-w-[160px]">
          <Label htmlFor="notif-kind">Kind</Label>
          <select id="notif-kind" value={kind} onChange={(e) => setKind(e.target.value)}
            className="w-full h-9 px-2 rounded-md border bg-background text-[13px]"
            data-testid="notif-kind-filter">
            <option value="">All kinds</option>
            {Object.entries(data.by_kind || {}).map(([k, v]) => (
              <option key={k} value={k}>{k} ({v.count})</option>
            ))}
          </select>
        </div>
        <label className="inline-flex items-center gap-1.5 text-[12px] pb-1">
          <input type="checkbox" checked={unreadOnly}
            onChange={(e) => setUnreadOnly(e.target.checked)}
            data-testid="notif-unread-only"/>
          Unread only
        </label>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="notif-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="notif-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">User</th>
              <th className="px-3 py-2">Kind</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Read</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`notif-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 mono text-[11px]">{(r.user_id || "").slice(0, 16)}…</td>
                <td className="px-3 py-2 text-[11.5px]">{r.kind || "—"}</td>
                <td className="px-3 py-2 max-w-md truncate">{r.title || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`chip text-[10.5px] ${r.read
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                    {r.read ? "read" : "unread"}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => resend(r.id)}
                    data-testid={`notif-resend-${r.id}`}>
                    <Send className="w-3 h-3 mr-1"/>Resend
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No notifications</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * 2. Messages (message_threads admin mediation)
 * ──────────────────────────────────────────────────────────────────── */
function MessagesTab() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [busy, setBusy] = useState(false);
  const [activeTid, setActiveTid] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/messages/threads?limit=100");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load threads");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] text-foreground/55">Total threads: {data.total}</span>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="msg-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="msg-threads-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Last activity</th>
              <th className="px-3 py-2">Buyer</th>
              <th className="px-3 py-2">Seller</th>
              <th className="px-3 py-2">Preview</th>
              <th className="px-3 py-2">Unread</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.id} data-testid={`msg-thread-row-${r.id}`}>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.last_message_at ? new Date(r.last_message_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 mono text-[11px]">{(r.buyer_id || "").slice(0, 14)}…</td>
                <td className="px-3 py-2 mono text-[11px]">{(r.seller_user_id || "").slice(0, 14)}…</td>
                <td className="px-3 py-2 max-w-md truncate text-foreground/65">{r.last_message_preview || "—"}</td>
                <td className="px-3 py-2 text-[11px] text-foreground/55">
                  {(r.unread_buyer || 0) + (r.unread_seller || 0)}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setActiveTid(r.id)}
                    data-testid={`msg-mediate-${r.id}`}>Mediate</Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No threads</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <MediateDialog tid={activeTid} onClose={() => setActiveTid(null)} onSaved={load}/>
    </div>
  );
}


function MediateDialog({ tid, onClose, onSaved }) {
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!tid;

  useEffect(() => {
    if (!tid) return;
    setText(""); setReason(""); setThread(null);
    api.get(`/admin/messages/threads/${tid}`).then(r => setThread(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [tid]);

  const submit = async () => {
    if (reason.trim().length < 3 || !text.trim()) {
      toast.error("Both text and reason (≥ 3 chars) are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/messages/threads/${tid}/admin-message`,
                      { text: text.trim(), reason: reason.trim() });
      toast.success("Mediation message posted");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl" data-testid="msg-mediate-dialog">
        <DialogHeader>
          <DialogTitle>Mediate thread</DialogTitle>
          <DialogDescription>
            Posts as verqo Admin · both buyer and seller will see the message.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {thread && (
            <div className="rounded-md border bg-foreground/3 p-2 text-[11.5px] max-h-40 overflow-y-auto">
              {(thread.messages || []).slice(0, 5).map(m => (
                <div key={m.id} className="py-1">
                  <span className="mono text-foreground/55 mr-1.5">[{m.author_role}]</span>
                  <span className="font-medium">{m.author_name}:</span> {m.body}
                </div>
              ))}
              {!(thread.messages || []).length && (
                <div className="text-foreground/45">No messages yet.</div>
              )}
            </div>
          )}
          <div>
            <Label htmlFor="msg-text">Mediation message</Label>
            <Textarea id="msg-text" rows={3} value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="msg-mediate-text"/>
          </div>
          <div>
            <Label htmlFor="msg-reason">Internal reason (min 3 chars · audit-logged)</Label>
            <Textarea id="msg-reason" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="msg-mediate-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3 || !text.trim()}
            data-testid="msg-mediate-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Post
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * 3. WMS
 * ──────────────────────────────────────────────────────────────────── */
function WmsTab() {
  const [data, setData] = useState({ rows: [], total: 0, by_vendor: {} });
  const [logs, setLogs] = useState({ rows: [], total: 0, failed: 0 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/admin/wms?limit=100"),
        api.get("/admin/wms/logs?since_days=7&limit=200"),
      ]);
      setData(d.data); setLogs(l.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load WMS data");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const disconnect = async (sellerId) => {
    const reason = window.prompt("Reason for force-disconnecting WMS? (min 3 chars · audit-logged)");
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    try {
      await api.post(`/admin/wms/${sellerId}/force-disconnect`, { reason: reason.trim() });
      toast.success("WMS disconnected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <Kpi label="Connections" value={data.total ?? 0}/>
        <Kpi label="Vendors" value={Object.keys(data.by_vendor || {}).length}/>
        <Kpi label="Forwards (7d)" value={logs.total ?? 0}/>
        <Kpi label="Failed (7d)" value={logs.failed ?? 0}
              tone={logs.failed > 0 ? "rose" : "emerald"}
              testid="wms-failed-tile"/>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(data.by_vendor || {}).map(([k, v]) => (
            <span key={k} className="chip text-[10.5px] bg-foreground/5 border-foreground/10">
              {k}: {v}
            </span>
          ))}
        </div>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="wms-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="wms-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">Seller</th>
              <th className="px-3 py-2">Vendor</th>
              <th className="px-3 py-2">Endpoint</th>
              <th className="px-3 py-2">Auto fwd</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.rows.map(r => (
              <tr key={r.seller_id} data-testid={`wms-row-${r.seller_id}`}>
                <td className="px-3 py-2">{r.seller?.email || r.seller_id}</td>
                <td className="px-3 py-2 font-medium">{r.vendor}</td>
                <td className="px-3 py-2 mono text-[11px] max-w-xs truncate">{r.endpoint_url}</td>
                <td className="px-3 py-2">{r.auto_forward ? "yes" : "no"}</td>
                <td className="px-3 py-2 mono text-[11px]">
                  {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => disconnect(r.seller_id)}
                    data-testid={`wms-disconnect-${r.seller_id}`}>
                    <ShieldOff className="w-3 h-3 mr-1"/>Disconnect
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-foreground/45">No WMS connections</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {logs.rows && logs.rows.length > 0 && (
        <details className="rounded-xl border" data-testid="wms-logs-details">
          <summary className="px-3 py-2 text-[12.5px] font-medium cursor-pointer">
            Forward log (last 7 days · {logs.rows.length} rows)
          </summary>
          <div className="border-t max-h-96 overflow-auto">
            <table className="w-full text-[11.5px]" data-testid="wms-logs-table">
              <thead className="bg-foreground/5 text-left text-[10px] uppercase tracking-wider text-foreground/55">
                <tr>
                  <th className="px-3 py-1.5">When</th>
                  <th className="px-3 py-1.5">Seller</th>
                  <th className="px-3 py-1.5">Vendor</th>
                  <th className="px-3 py-1.5">Order</th>
                  <th className="px-3 py-1.5">Event</th>
                  <th className="px-3 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {logs.rows.map(l => (
                  <tr key={l.id}>
                    <td className="px-3 py-1 mono text-[10.5px]">
                      {l.at ? new Date(l.at).toLocaleString() : "—"}
                    </td>
                    <td className="px-3 py-1 mono text-[10.5px]">{(l.seller_id || "").slice(0, 12)}…</td>
                    <td className="px-3 py-1">{l.vendor}</td>
                    <td className="px-3 py-1">{l.order_code}</td>
                    <td className="px-3 py-1">{l.event}</td>
                    <td className="px-3 py-1">
                      <span className={`chip text-[10px] ${l.status === "ok" || (l.response_code && l.response_code < 400)
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                        {l.status} {l.response_code ?? ""}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * 4. Crons
 * ──────────────────────────────────────────────────────────────────── */
function CronsTab() {
  const [data, setData] = useState({ crons: [] });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/crons");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load crons");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const trigger = async (name) => {
    const reason = window.prompt(`Reason for manual triggering ${name}? (min 3 chars · audit-logged)`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Reason must be ≥ 3 chars");
      return;
    }
    const t0 = Date.now();
    try {
      const r = await api.post(`/admin/crons/${name}/trigger`, { reason: reason.trim() });
      const ms = Date.now() - t0;
      toast.success(`${name}: ${r.data.status} in ${r.data.duration_ms || ms}ms`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trigger failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] text-foreground/55">Registry: {data.crons.length} crons</span>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="crons-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RotateCw className="w-3.5 h-3.5 mr-1"/>}Refresh
        </Button>
      </div>
      <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-3" data-testid="crons-grid">
        {data.crons.map(c => {
          const lr = c.last_run;
          const succeeded = lr?.status === "succeeded";
          return (
            <div key={c.name} className="rounded-xl border p-3.5 space-y-2 bg-background"
                  data-testid={`cron-card-${c.name}`}>
              <div className="flex items-center justify-between">
                <div className="font-medium text-[13px]">{c.name}</div>
                <span className="chip text-[10.5px] bg-foreground/5 border-foreground/10">
                  {c.cadence_hint}
                </span>
              </div>
              <p className="text-[11.5px] text-foreground/55">{c.description}</p>
              {lr ? (
                <div className="rounded-md bg-foreground/3 p-2 text-[10.5px] space-y-1">
                  <div className="flex items-center gap-2">
                    <span className={`chip text-[10px] ${succeeded
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-rose-50 border-rose-200 text-rose-800"}`}>
                      {lr.status}
                    </span>
                    <span className="mono text-foreground/55">{lr.duration_ms ?? "?"}ms</span>
                  </div>
                  <div className="mono text-foreground/55">
                    last: {lr.started_at ? new Date(lr.started_at).toLocaleString() : "—"}
                  </div>
                  {lr.error && (
                    <div className="text-rose-700 mono">err: {lr.error.slice(0, 80)}</div>
                  )}
                </div>
              ) : (
                <div className="rounded-md bg-foreground/3 p-2 text-[10.5px] text-foreground/45">
                  Never run manually from this console.
                </div>
              )}
              <Button size="sm" onClick={() => trigger(c.name)}
                data-testid={`cron-trigger-${c.name}`} className="w-full">
                <Play className="w-3 h-3 mr-1"/>Trigger now
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ────────────────────────────────────────────────────────────────────
 * 5. Apps catalog
 * ──────────────────────────────────────────────────────────────────── */
function AppsCatalogTab() {
  const [data, setData] = useState({ apps: [], total: 0 });
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/apps-catalog");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load catalog");
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-[11.5px] text-foreground/55">
          Catalog has {data.total} apps · {(data.apps || []).filter(a => a._override).length} have runtime overrides
        </span>
        <Button variant="outline" onClick={load} disabled={busy} data-testid="apps-refresh">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : "Refresh"}
        </Button>
      </div>
      <div className="rounded-xl border overflow-hidden">
        <table className="w-full text-[12.5px]" data-testid="apps-table">
          <thead className="bg-foreground/5 text-left text-[11px] uppercase tracking-wider text-foreground/55">
            <tr>
              <th className="px-3 py-2">App</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Audience</th>
              <th className="px-3 py-2">Coming soon</th>
              <th className="px-3 py-2">Default</th>
              <th className="px-3 py-2 text-right">Installs</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.apps.map(a => (
              <tr key={a.id} data-testid={`apps-row-${a.id}`}>
                <td className="px-3 py-2 font-medium">
                  {a.name}
                  {a._override && (
                    <span className="ml-1.5 chip text-[9.5px] bg-amber-50 border-amber-200 text-amber-800">
                      OVERRIDE
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-foreground/65">{a.category}</td>
                <td className="px-3 py-2">{a.audience}</td>
                <td className="px-3 py-2">
                  {a.coming_soon
                    ? <span className="chip text-[10.5px] bg-amber-50 border-amber-200 text-amber-800">global</span>
                    : (a.coming_soon_for && a.coming_soon_for.length)
                      ? <span className="chip text-[10.5px] bg-sky-50 border-sky-200 text-sky-800">
                          {a.coming_soon_for.join(",")}
                        </span>
                      : <span className="text-foreground/45">—</span>}
                </td>
                <td className="px-3 py-2">
                  {a.default ? "yes" : "no"}{a.default_both ? " · both" : ""}
                </td>
                <td className="px-3 py-2 text-right mono">{a.install_count ?? 0}</td>
                <td className="px-3 py-2 text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(a)}
                    data-testid={`apps-edit-${a.id}`}>
                    <Pencil className="w-3 h-3 mr-1"/>Edit
                  </Button>
                </td>
              </tr>
            ))}
            {data.apps.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-foreground/45">No apps</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <CatalogEditDialog app={editing} onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}/>
    </div>
  );
}


function CatalogEditDialog({ app, onClose, onSaved }) {
  const [comingSoon, setComingSoon] = useState(false);
  const [comingSoonFor, setComingSoonFor] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [defaultBoth, setDefaultBoth] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const open = !!app;

  useEffect(() => {
    if (!app) return;
    setComingSoon(!!app.coming_soon);
    setComingSoonFor((app.coming_soon_for || []).join(","));
    setIsDefault(!!app.default);
    setDefaultBoth(!!app.default_both);
    setReason("");
  }, [app]);

  const submit = async () => {
    if (reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars · audit-logged)");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        reason: reason.trim(),
        coming_soon: comingSoon,
        coming_soon_for: comingSoonFor
          ? comingSoonFor.split(",").map(s => s.trim()).filter(Boolean)
          : [],
        default: isDefault,
        default_both: defaultBoth,
      };
      await api.patch(`/admin/apps-catalog/${app.id}`, payload);
      toast.success("App catalog override saved");
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="apps-edit-dialog">
        <DialogHeader>
          <DialogTitle>Override · {app?.name}</DialogTitle>
          <DialogDescription>
            Changes apply at runtime — every buyer/seller call to /apps sees them immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <label className="flex items-center gap-2 text-[12.5px]">
            <input type="checkbox" checked={comingSoon}
              onChange={(e) => setComingSoon(e.target.checked)}
              data-testid="apps-edit-coming-soon"/>
            Coming soon (globally hide install button)
          </label>
          <div>
            <Label htmlFor="cs-for">Coming soon for (comma-separated roles)</Label>
            <Input id="cs-for" value={comingSoonFor}
              onChange={(e) => setComingSoonFor(e.target.value)}
              placeholder="e.g. seller,buyer"
              data-testid="apps-edit-coming-soon-for"/>
          </div>
          <label className="flex items-center gap-2 text-[12.5px]">
            <input type="checkbox" checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              data-testid="apps-edit-default"/>
            Default-install for audience role
          </label>
          <label className="flex items-center gap-2 text-[12.5px]">
            <input type="checkbox" checked={defaultBoth}
              onChange={(e) => setDefaultBoth(e.target.checked)}
              data-testid="apps-edit-default-both"/>
            Default-install for both buyer + seller
          </label>
          <div>
            <Label htmlFor="cat-reason">Reason (min 3 chars · audit-logged)</Label>
            <Textarea id="cat-reason" rows={2} value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="apps-edit-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3}
            data-testid="apps-edit-submit">
            {busy && <Loader2 className="w-3 h-3 mr-1 animate-spin"/>}Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
